/**
 * AdminBugReports — /admin/bug-reports
 * Admin queue for user-submitted bug reports.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import ToolSelector from '../../components/ToolSelector';
import { Button } from '../../components/ui/button';
import { Bug, CheckCircle2, Loader2, Filter, ExternalLink } from 'lucide-react';

const STATUS_STYLE = {
  open:         'bg-red-100 text-red-800',
  triage:       'bg-amber-100 text-amber-800',
  in_progress:  'bg-primary/10 text-primary',
  resolved:     'bg-emerald-100 text-emerald-800',
  wont_fix:     'bg-muted text-muted-foreground',
};

const AdminBugReports = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const [type, setType] = useState('');       // '' = all, 'bug', 'support'
  const [loading, setLoading] = useState(true);
  const [openCount, setOpenCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: '200' });
      if (type) params.append('type', type);
      const { data } = await axios.get(`${API}/admin/bug-reports?${params.toString()}`);
      setRows(data.bugs || []);
      setOpenCount(data.open_count || 0);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [API, status, type]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, newStatus, priority) => {
    try {
      await axios.put(`${API}/admin/bug-reports/${id}`, { status: newStatus, priority: priority || 'medium' });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-bug-reports-page">
      <ToolSelector />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Bug className="w-3 h-3" /> Admin · Bugs & Support Tickets
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-1">User Feedback Inbox</h1>
          </div>
          {openCount > 0 && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full bg-red-500 text-white" data-testid="admin-bug-reports-open-count">
              {openCount} open
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="admin-bug-reports-filter">
            <option value="open">Open</option>
            <option value="triage">Triage</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="wont_fix">Won&apos;t fix</option>
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="admin-bug-reports-type-filter">
            <option value="">All types</option>
            <option value="bug">Bugs</option>
            <option value="support">Support tickets</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="pro-card p-6 text-center text-sm text-muted-foreground italic">No bugs in this state.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((b) => (
              <div key={b.id} className="pro-card p-4" data-testid={`admin-bug-row-${b.id}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[b.status] || ''}`}>{b.status}</span>
                  {b.type === 'support' ? (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">Support</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-600">Bug</span>
                  )}
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{b.priority}</span>
                  <span className="text-[11px] text-muted-foreground ml-auto">{b.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="font-heading font-extrabold text-base">{b.title}</div>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{b.description}</p>
                {b.steps_to_repro && (
                  <div className="mt-2 text-xs">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">Steps</div>
                    <div className="whitespace-pre-wrap">{b.steps_to_repro}</div>
                  </div>
                )}
                {b.page_url && (
                  <a href={b.page_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2">
                    {b.page_url} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {b.screenshot_url && (
                  <div className="mt-2">
                    <img src={b.screenshot_url} alt="Screenshot" className="max-h-64 rounded-lg border border-border" loading="lazy" />
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-2">Reported by {b.reporter_email}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.status !== 'triage' && b.status !== 'resolved' && (
                    <Button size="sm" onClick={() => update(b.id, 'triage')} className="rounded-full font-bold" data-testid={`admin-bug-triage-${b.id}`}>Move to triage</Button>
                  )}
                  {b.status !== 'in_progress' && b.status !== 'resolved' && (
                    <Button size="sm" variant="outline" onClick={() => update(b.id, 'in_progress', 'high')} className="rounded-full" data-testid={`admin-bug-inprogress-${b.id}`}>Start work</Button>
                  )}
                  {b.status !== 'resolved' && (
                    <Button size="sm" variant="outline" onClick={() => update(b.id, 'resolved')} className="rounded-full" data-testid={`admin-bug-resolve-${b.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Mark resolved
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBugReports;
